// src/services/categoriesApi.js
import axios from 'axios';
import {getJwtToken} from "../utils/auth";
import {translateToEnglish, translateToSpanish} from "../utils/translations";
import {getBaseUrl} from "../utils/apiClient";

const categoriesApiClient = axios.create({
    baseURL: `${getBaseUrl()}/categories`,
});

categoriesApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken(); // Espera a que se obtenga el token
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    //config.headers['bypass-tunnel-reminder'] = 'true';
    return config;
}, (error) => {
    return Promise.reject(error); // Si hay un error, lo propagamos
});

export const getCategories = async (type, location) => {
    try {
        const response = await categoriesApiClient.get(`/${translateToEnglish(type)}`, {
            params: { location }  // Se envía location como query parameter
        });
        return response.data.map(category => ({
            ...category,
            type: translateToSpanish(category.type)
        }));
    } catch (error) {
        throw error;
    }
};

export const getAllCategories = async (location) => {
    try {
        // Obtener categorías de servicios y productos en paralelo
        const [servicesResponse, productsResponse] = await Promise.all([
            categoriesApiClient.get('/service', { params: { location } }),
            categoriesApiClient.get('/products', { params: { location } })
        ]);

        // Combinar ambos arrays y traducir los tipos
        return [
            ...servicesResponse.data.map(category => ({
                ...category,
                type: translateToSpanish(category.type)
            })),
            ...productsResponse.data.map(category => ({
                ...category,
                type: translateToSpanish(category.type)
            }))
        ];
    } catch (error) {
        throw error;
    }
};


export const getCategory = async (type, name) => {
    try {
        const response = await categoriesApiClient.get(`/${translateToEnglish(type)}/${name.trim()}`);
        return {
            ...response.data,
            type: translateToSpanish(response.data.type)
        };
    } catch (error) {
        throw error;
    }
};

export const addCategory = async (category) => {
    try {
        const categoryTranslated = {
            ...category,
            type: translateToEnglish(category.type)
        };
        const response = await categoriesApiClient.post(``, categoryTranslated);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getSearchResults = async () => {
    try {
        const response = await categoriesApiClient.get("/search");
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const listCategories = async () => {
    try {
        const response = await categoriesApiClient.get("/list-all");
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const updateCategory = async (id, category) => {
    try {
        const response = await categoriesApiClient.put(`/id/${id}`, category);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const deleteCategory = async (id) => {
    try {
        const response = await categoriesApiClient.delete(`/id/${id}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * Incrementa el contador de vistas de una categoría específica
 * @param {string} categoryId - ID de la categoría
 * @returns {Promise} - Promise que resuelve cuando se completa la petición
 */
export const incrementCategoryViews = async (categoryId) => {
    try {
        await categoriesApiClient.post(`/${categoryId}/view`);
        return true;
    } catch (error) {
        console.warn("Error al registrar vista de categoría:", error);
        // No lanzamos el error para que sea transparente para el usuario
        return false;
    }
};

/**
 * Obtiene las estadísticas de una categoría específica
 * Incluye totales históricos, semana actual y semana anterior
 * @param {string} categoryId - ID de la categoría
 * @returns {Promise} - Promise con las estadísticas de la categoría
 */
export const getCategoryStats = async (categoryId) => {
    try {
        const response = await categoriesApiClient.get(`/${categoryId}/stats`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Obtiene las estadísticas de múltiples categorías
 * Útil para dashboards que muestran comparativas entre categorías
 * @param {string[]} categoryIds - Array de IDs de categorías
 * @returns {Promise} - Promise con las estadísticas de las categorías
 */
export const getMultipleCategoryStats = async (categoryIds) => {
    try {
        const response = await categoriesApiClient.get('/stats/multiple', {
            params: { categoryIds }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Obtiene todas las categorías en formato simplificado (name, type, label)
 * No requiere autenticación
 * @returns {Promise} - Promise con array de categorías en formato [{label, name, type}]
 */
export const getCategoriesNameType = async () => {
    try {
        const response = await categoriesApiClient.get('/name-type');
        return response.data;
    } catch (error) {
        throw error;
    }
};

export default categoriesApiClient;